function hasPermission(array, permission) {
  if (array.includes(permission)) return true;
  return false;
}

function isFlexiridDashboard(payload) {
  const fields = [
    payload.isCarpoolAdmin,
    payload.isFinanceAdmin,
    payload.isPoolCarAdmin,
    payload.isShuttleAdmin,
    payload.isTaxiAdmin,
    payload.isVanpoolAdmin,
  ];
  if (payload.isFlexiRideAdmin && !fields.some((field) => field)) {
    return true;
  }
  return false;
}

function checkPersonalInfoNeeded(userDetails, companySettings) {
  if (!companySettings) return false;

  const { personnel } = userDetails;
  const hasBirthdateParam = companySettings.birthdateRequired === true;
  // Gender options come from companySettings.genders; with no options the
  // PersonalInfo screen cannot render the field, so don't gate on it.
  const hasGenderParam =
    companySettings.genderRequired === true &&
    Array.isArray(companySettings.genders) &&
    companySettings.genders.length > 0;
  const hasRaceParam = Array.isArray(companySettings.races) && companySettings.races.length > 0;

  // If none of the demographic params are on, the Personal Info screen is never shown.
  if (!hasBirthdateParam && !hasGenderParam && !hasRaceParam) {
    return false;
  }

  const birthdateMissing = hasBirthdateParam && !personnel?.birthdate;
  const genderMissing = hasGenderParam && !personnel?.gender;
  const raceMissing = hasRaceParam && !personnel?.race;

  if (birthdateMissing || genderMissing || raceMissing) {
    return 'PersonalInfo';
  }

  return false;
}

function checkRiderRedirects(userDetails, companySettings = null) {
  console.log('checking rider redirects', userDetails);
  if (userDetails.isRider === false) return false;

  if (userDetails?.accountType) {
    return false;
  }

  if (!userDetails.aggrementDate && userDetails.kvkkDocUrl?.length > 0) {
    return 'ApproveKvkk';
  }

  if (!userDetails.destination?.id) {
    return 'SelectMainCampus';
  }

  if (companySettings?.isDepartmentSelectable && (!userDetails.accountingCompany || !userDetails.department?.id)) {
    return 'selectCompany';
  }

  const isWorkgroupSelectable = companySettings?.isWorkgroupSelectable;
  const isEmptyWorkgroupInfo =
    userDetails.workgroupInfo === null || Object.keys(userDetails.workgroupInfo ?? {}).length === 0;

  if (isWorkgroupSelectable && userDetails.hasDefaultWorkgroup && isEmptyWorkgroupInfo) {
    return 'workgroupShiftTime';
  }

  // Demographic data (Personal Info) — collected after workgroup, before home address.
  // Covers both new register and existing-user-with-param-opened-later scenarios.
  const personalInfoRedirect = checkPersonalInfoNeeded(userDetails, companySettings);
  if (personalInfoRedirect) return personalInfoRedirect;

  if (!userDetails.personnel?.homeLocation?.latitude || !userDetails.personnel?.homeLocation?.longitude) {
    const isAddressSkipped = JSON.parse(sessionStorage.getItem('skipAddress'));
    if (isAddressSkipped) return false;
    return 'HomeAddress';
  }

  return false;
}

function resolveRedirectRoute(userDetails = {}, companySettings = null) {
  const redirectedPage = checkRiderRedirects(userDetails, companySettings);
  if (redirectedPage) {
    return { name: redirectedPage };
  }

  if (userDetails.mainPage) {
    console.log('redirecting to main page');
    const router = JSON.parse(userDetails.mainPage);

    if (router?.data?.legacyRoute) {
      return { name: 'legacy', params: { route: router.data.legacyRoute } };
    }

    const routeObject = { name: router.route };
    if (router.data?.typeParam) {
      routeObject.params = { type: router.data.typeParam };
    }

    return routeObject;
  }

  if (userDetails.isShuttleAdmin) {
    console.log('redirecting to flexiride dashboard becuase isShuttleAdmin is true');
    if (window.location.origin.includes('portal')) {
      return { name: 'FlexiShuttleDashboardUs' };
    }
    return { name: 'Home' };
  }

  if (userDetails.isVanpoolAdmin) {
    console.log('redirecting to flexiride dashboard becuase isVanpoolAdmin is true');
    return { name: 'FlexiVanpoolDashboard' };
  }

  if (isFlexiridDashboard(userDetails) && hasPermission(userDetails.menuPermissionList, 'vser-dashboard')) {
    console.log('redirecting to flexiride dashboard becuase isFlexiridDashboard is true');
    return { name: 'FlexiVanpoolDashboard' };
  }

  if (userDetails.isFlexiRideAdmin) {
    console.log('redirecting to flexiride dashboard becuase isFlexiRideAdmin is true');
    return { name: 'flexiRideDashboard' };
  }

  if (!userDetails.accountType) {
    if (userDetails.personnel?.isSharedRideUser) {
      console.log('redirecting to request becuase isSharedRideUser is true');
      return { name: 'SharedRideRequest' };
    }
    if (userDetails.personnel?.isFlexirideAssistant) {
      console.log('redirecting to FlexirideRequests becuase isFlexirideAssistant is true');
      return { name: 'FlexirideRequests' };
    }
    console.log('redirecting to Shuttle Information becuase isVanpoolUser or isShuttleUser is true');
    const route = 'app.personel.info'; // TICKET
    return { name: 'legacy', params: { route } };
  }

  console.log('falling back to default home page');
  return { name: 'Home' };
}

export default {
  name: 'loginRedirect',
  methods: {
    loginRedirect(userResponse, companySettings = null) {
      const targetRoute = resolveRedirectRoute(
        userResponse,
        companySettings ?? this.$store?.state?.companySettings ?? null,
      );

      if (!targetRoute) {
        return;
      }

      const shellName = this.$store?.state?.shell;
      // If flexigo shell is active, let the shell handle navigation after loading
      // Otherwise navigate immediately (for public pages, etc.)
      if (shellName === 'flexigo' && this.$route?.name === 'login') {
        console.log('Flexigo shell will handle redirect after loading completes');

        // Add a fallback redirect after a delay to ensure navigation happens
        // This handles cases where the shell might not properly detect the need to redirect
        setTimeout(() => {
          // Check if we're still on the login page after shell should have redirected
          this.$router.push(targetRoute).catch((err) => {
            if (err.name !== 'NavigationDuplicated') {
              console.error('Fallback navigation error:', err);
            }
          });
        }, 1000); // Wait 1 second for shell to handle redirect, then fallback if needed

        return;
      }

      // Catch navigation errors to prevent console warnings
      this.$router.push(targetRoute).catch((err) => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    },
    checkRiderRedirects,
  },
};

export { checkRiderRedirects };
export { resolveRedirectRoute };
export { checkPersonalInfoNeeded };
